import React, { useState, useEffect, useRef, useCallback } from "react";

/** Inlined from react-audio-voice-recorder (package bundles React 18 internals, incompatible with React 19) */
function useAudioRecorder(audioConstraints) {
  const t = useT();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState();
  const [timerInterval, setTimerInterval] = useState();
  const [recordingBlob, setRecordingBlob] = useState();

  const startTimer = useCallback(() => {
    const interval = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
    setTimerInterval(interval);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerInterval != null) clearInterval(timerInterval);
    setTimerInterval(undefined);
  }, [timerInterval]);

  const startRecording = useCallback(() => {
    if (timerInterval != null) return;
    navigator.mediaDevices
      .getUserMedia({ audio: audioConstraints ?? true })
      .then((stream) => {
        setIsRecording(true);
        const recorder = new MediaRecorder(stream);
        setMediaRecorder(recorder);
        recorder.start();
        startTimer();
        recorder.addEventListener("dataavailable", (event) => {
          setRecordingBlob(event.data);
          recorder.stream.getTracks().forEach((track) => track.stop());
          setMediaRecorder(undefined);
        });
      })
      .catch((error) => {
        console.log(error.name, error.message, error.cause);
        toast.error(
          error?.name === "NotAllowedError"
            ? t("studioOptions.mic.denied")
            : t("studioOptions.mic.notFound"),
        );
      });
  }, [timerInterval, audioConstraints, startTimer, t]);

  const stopRecording = useCallback(() => {
    mediaRecorder?.stop();
    stopTimer();
    setRecordingTime(0);
    setIsRecording(false);
    setIsPaused(false);
  }, [mediaRecorder, stopTimer]);

  return {
    startRecording,
    stopRecording,
    recordingBlob,
    isRecording,
    isPaused,
    recordingTime,
  };
}
import axios from "axios";
import { getCurrentUser, requireIdToken } from "../../utils/firebaseAuth";
import { Button, SecondaryButton, toast } from "@jammable/ui-core";
import { Spinner } from "@jammable/ui-components/Loader";
import { FaStopCircle, FaPlay, FaPause, FaCheck } from "react-icons/fa";
import { IoIosMicrophone } from "react-icons/io";
import styles from "../../styles/Conversion.module.css"; // Ensure path is correct
import { useModal } from "@/context/ModalContext";
import dynamic from "next/dynamic";
import { Mic, Pause, Play, RotateCcw, Square } from "lucide-react";
import { useT } from "@jammable/ui-core/src/i18n";

// Only opened when they are out of credits (pricing, Stripe Elements).
const UpgradeModal = dynamic(() => import("./UpgradeModal"), { ssr: false });

// `onNeedsAuth` (optional): recording works logged out; using the take
// needs an account, so it is called with a retry instead of failing.
// `variant="jam"`: the /create studio's design language F look. Same logic,
// different markup; CreateModal keeps the default.
// `onTake(blob, seconds)` (optional): hand the recording back instead of
// uploading it (the train page adds it to its samples), then get ready for
// the next one. `takeLabel` and `hint` reword the button and the line under
// the mic for that use.
const MicrophoneRecordView = ({
  onSongSelected,
  onError,
  onNeedsAuth,
  variant,
  onTake,
  takeLabel,
  hint,
}) => {
  const jam = variant === "jam";
  const t = useT();
  // --- Microphone State ---
  const [micLoading, setMicLoading] = useState(false); // Loading specific to mic upload
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null); // URL for previewing recording
  const [recordingBlobForUpload, setRecordingBlobForUpload] = useState(null); // The blob to be uploaded
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [recordedDuration, setRecordedDuration] = useState(0); // Track the duration of completed recording
  const audioRef = useRef(null);

  const { openModal } = useModal();

  const {
    startRecording,
    stopRecording,
    recordingBlob,
    isRecording: isMicRecording,
    isPaused: isMicPaused,
    recordingTime: micRecordingTime,
  } = useAudioRecorder({
    // Add error handling if needed, e.g., onNotAllowedOrFound
    // onNotAllowedOrFound: (error) => {
    //   console.error("Microphone access error:", error);
    //   toast.error("Microphone access denied or not found.");
    //   onError?.("Microphone access denied or not found.", error);
    // }
  });
  // --- End Microphone State ---

  // --- Audio Playback Ref Cleanup ---
  useEffect(() => {
    // Cleanup function runs on component unmount
    return () => {
      audioRef.current?.pause(); // Stop playback
      if (audioRef.current) {
        audioRef.current.onended = null; // Remove listener
      }
      audioRef.current = null;
      // URL revocation is handled in the other effect's cleanup
    };
  }, []);

  // --- Microphone Effects ---
  // Handle microphone recording blob when it's ready
  useEffect(() => {
    if (!recordingBlob || micLoading) return;

    let currentAudioPreviewUrl = null; // Store the URL locally for cleanup

    const processBlob = async () => {
      try {
        // Loaded once there's a recording: with its Node Buffer polyfill it
        // was ~120 KB on /train for everyone who never presses Record.
        const { default: fixWebmDuration } = await import("webm-duration-fix");
        const fixedBlob = await fixWebmDuration(recordingBlob);
        setRecordingBlobForUpload(fixedBlob);
        const url = URL.createObjectURL(fixedBlob);
        currentAudioPreviewUrl = url; // Assign to local variable
        setAudioPreviewUrl(url);
      } catch (error) {
        console.error("Error fixing webm duration or creating URL:", error);
        setRecordingBlobForUpload(recordingBlob); // Fallback to original blob
        const url = URL.createObjectURL(recordingBlob);
        currentAudioPreviewUrl = url; // Assign fallback URL
        setAudioPreviewUrl(url);
        onError?.("Could not process recording.", error);
      }
    };

    processBlob();

    // Cleanup function for the effect using the local variable
    return () => {
      if (currentAudioPreviewUrl) {
        // Clean up the audio element as well when blob changes
        audioRef.current?.pause();
        if (audioRef.current) {
          audioRef.current.onended = null;
        }
        audioRef.current = null;
        setIsPreviewPlaying(false);
        URL.revokeObjectURL(currentAudioPreviewUrl);
      }
    };
  }, [recordingBlob, micLoading, onError]);

  // Effect to setup Audio object when URL is ready
  useEffect(() => {
    if (audioPreviewUrl) {
      audioRef.current = new Audio(audioPreviewUrl);
      audioRef.current.onended = () => {
        setIsPreviewPlaying(false);
      };
      // Get and set the duration of the recorded audio
      audioRef.current.onloadedmetadata = () => {
        setRecordedDuration(Math.round(audioRef.current.duration));
      };
    } else {
      // Ensure cleanup if URL becomes null
      audioRef.current?.pause();
      if (audioRef.current) {
        audioRef.current.onended = null;
      }
      audioRef.current = null;
      setIsPreviewPlaying(false);
      setRecordedDuration(0);
    }
  }, [audioPreviewUrl]);
  // --- End Microphone Effects ---

  // --- Microphone Upload Logic ---
  const handleMicUpload = async () => {
    if (!recordingBlobForUpload || micLoading) return;
    if (onTake) {
      onTake(recordingBlobForUpload, recordedDuration);
      handleStopAndClearMic();
      return;
    }
    if (onNeedsAuth && !(await getCurrentUser())) {
      onNeedsAuth(handleMicUpload);
      return;
    }

    setMicLoading(true);

    try {
      const token = await requireIdToken();

      const presignedResponse = await axios
        .get("/api/s3/modelS3PresignedUrl", {
          headers: { Authorization: token },
        })
        .catch((err) => {
          throw new Error(
            err.response?.data?.message || t("studioOptions.mic.prepareFailed"),
          );
        });

      const { url: uploadUrl, absoluteUrl } = presignedResponse.data;

      await axios
        .put(uploadUrl, recordingBlobForUpload, {
          headers: {
            "Content-Type": recordingBlobForUpload.type || "audio/webm",
          },
        })
        .catch((err) => {
          throw new Error(
            err.response?.data?.message || t("studioOptions.mic.fileFailed"),
          );
        });

      const fileName = `Recording_${new Date().toISOString()}.webm`;
      onSongSelected({
        url: absoluteUrl,
        title: fileName,
        metadata: { source: "microphone" },
      });

      // Reset state after successful selection/upload is handled by parent unmounting this view
      // setMicLoading(false); // Already handled by parent
    } catch (err) {
      console.error("Microphone upload failed:", err);
      setMicLoading(false);
      const message = err.message || t("studioOptions.mic.uploadFailed");

      if (message === "You are out of credits") {
        openModal(
          "upgrade-modal",
          <UpgradeModal
            from="microphone-record-view"
            dailyLimit={false}
            message="You are out of credits"
          />,
          { className: "tw-p-0" },
        );
      } else {
        toast.error(message);
      }
      onError?.(message, err);
    }

    // Stop playback if it was playing before upload
    audioRef.current?.pause();
    setIsPreviewPlaying(false);
  };

  const handleStopAndClearMic = () => {
    if (isMicRecording) {
      stopRecording();
    }
    // Also stop and clean up audio playback state
    audioRef.current?.pause();
    setIsPreviewPlaying(false);
    setRecordingBlobForUpload(null);
    setAudioPreviewUrl(null); // This will trigger the cleanup effect
    setMicLoading(false); // Ensure loading state is reset if clearing
  };

  // --- Audio Playback Control ---
  const togglePreviewPlayback = () => {
    if (!audioRef.current) return;

    if (isPreviewPlaying) {
      audioRef.current.pause();
      setIsPreviewPlaying(false);
    } else {
      audioRef.current
        .play()
        .then(() => {
          setIsPreviewPlaying(true);
        })
        .catch((err) => {
          console.error("Error playing audio preview:", err);
          toast.error(t("studioOptions.mic.previewFailed"));
          setIsPreviewPlaying(false); // Reset state on error
        });
    }
  };
  // --- End Audio Playback Control ---

  const formatSeconds = (s) => {
    const minutes = Math.floor(s / 60);
    const seconds = s % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };
  // --- End Microphone Upload Logic ---

  const toggleRecording = () => {
    if (isMicRecording) {
      stopRecording();
    } else {
      handleStopAndClearMic(); // Clear previous state if any
      startRecording();
    }
  };

  if (jam) {
    const hasTake = !!audioPreviewUrl && !isMicRecording;
    return (
      <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-4 tw-rounded-[22px] tw-bg-jam-lav tw-px-5 tw-py-7 tw-text-jam-ink">
        {!hasTake && (
          <div className="tw-relative tw-flex tw-size-40 tw-items-center tw-justify-center">
            {isMicRecording && (
              <>
                {!isMicPaused && (
                  <span
                    aria-hidden
                    className="tw-absolute tw-inset-3 tw-animate-ping tw-rounded-full tw-bg-jam-purple/20"
                  />
                )}
                <span
                  aria-hidden
                  className="tw-absolute tw-inset-0 tw-rounded-full tw-border-[3px] tw-border-solid tw-border-jam-soft"
                />
              </>
            )}
            <button
              type="button"
              onClick={toggleRecording}
              aria-label={
                isMicRecording
                  ? t("studioOptions.mic.stop")
                  : t("studioOptions.mic.start")
              }
              className="tw-relative tw-flex tw-size-28 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-purple tw-text-white tw-transition hover:tw-bg-jam-deep active:tw-scale-95"
            >
              {isMicRecording ? (
                <Square size={34} fill="currentColor" strokeWidth={0} />
              ) : (
                <Mic size={46} strokeWidth={2.25} />
              )}
            </button>
          </div>
        )}

        {/* Timer */}
        <span className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-white tw-px-4 tw-py-1.5 tw-text-sm tw-font-extrabold tw-tabular-nums">
          {isMicRecording && (
            <span className="tw-size-2 tw-animate-pulse tw-rounded-full tw-bg-jam-purple" />
          )}
          {recordingBlobForUpload
            ? formatSeconds(recordedDuration)
            : formatSeconds(micRecordingTime)}
        </span>

        {!hasTake && (
          <div className="tw-text-center tw-text-xs tw-font-semibold tw-text-jam-muted">
            {isMicRecording
              ? t("studioOptions.mic.tapStop")
              : hint || t("studioOptions.mic.tapRecord")}
          </div>
        )}

        {micLoading && !audioPreviewUrl && (
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
            <Spinner className="tw-border-jam-purple" size={6} />
            <span className="tw-text-sm tw-font-semibold tw-text-jam-muted">
              {t("studioOptions.mic.processing")}
            </span>
          </div>
        )}

        {hasTake && (
          <div className="tw-flex tw-w-full tw-flex-col tw-gap-2.5">
            <button
              type="button"
              onClick={togglePreviewPlayback}
              disabled={micLoading}
              aria-label={
                isPreviewPlaying
                  ? t("studioOptions.mic.pausePreview")
                  : t("studioOptions.mic.playPreview")
              }
              className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-full tw-border-0 tw-bg-white tw-p-2 tw-pr-5 tw-text-left tw-text-jam-ink disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
            >
              <span className="tw-flex tw-size-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-purple tw-text-white">
                {isPreviewPlaying ? (
                  <Pause size={18} fill="currentColor" strokeWidth={0} />
                ) : (
                  <Play size={18} fill="currentColor" strokeWidth={0} />
                )}
              </span>
              <span className="tw-text-[15px] tw-font-extrabold">
                {t("studioOptions.mic.yourRecording")}
              </span>
            </button>
            <button
              type="button"
              onClick={handleMicUpload}
              disabled={!recordingBlobForUpload || micLoading || isMicRecording}
              className="tw-flex tw-h-[52px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-lime tw-text-[16px] tw-font-extrabold tw-text-jam-ink tw-transition-colors hover:tw-bg-[#C4EE00] disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
            >
              {micLoading ? (
                <Spinner className="tw-border-jam-ink tw-border-3" size={4} />
              ) : (
                takeLabel || t("studioOptions.mic.use")
              )}
            </button>
            <button
              type="button"
              onClick={toggleRecording}
              disabled={micLoading}
              className="tw-flex tw-h-11 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-text-sm tw-font-extrabold tw-text-jam-ink tw-transition-colors hover:tw-border-jam-purple disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
            >
              <RotateCcw size={15} strokeWidth={2.5} />
              {t("studioOptions.mic.again")}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    // --- Microphone Recording UI ---
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
      <div
        className={`tw-w-60 tw-relative tw-h-60 tw-flex tw-items-center tw-justify-center tw-rounded-full ${isMicRecording ? `tw-border-2 tw-border-solid ${!isMicPaused ? styles["animate-ring2"] : "tw-border-purple-400"}` : ""}`}
      >
        <div
          className={`tw-w-48 tw-h-48 tw-flex tw-items-center tw-justify-center tw-rounded-full ${isMicRecording ? `tw-border-2 tw-border-solid ${!isMicPaused ? styles["animate-ring3"] : "tw-border-purple-700"}` : ""}`}
        >
          <div
            onClick={toggleRecording}
            className={`tw-w-40 tw-h-40 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-relative tw-cursor-pointer tw-transition-all tw-duration-150 tw-border-b-8 active:tw-translate-y-1 active:tw-border-b-4 active:tw-scale-[0.98] ${
              isMicRecording
                ? "tw-bg-red-500 hover:tw-bg-red-600 tw-border-red-700"
                : "tw-bg-purple-700 hover:tw-bg-purple-800 tw-border-purple-900"
            }`}
          >
            {isMicRecording ? (
              <FaStopCircle className="tw-text-white" size={60} />
            ) : (
              <IoIosMicrophone className="tw-text-white" size={100} />
            )}
          </div>
        </div>
        {!isMicRecording && !recordingBlobForUpload && (
          <div className="tw-text-lg tw-absolute tw-bottom-0 tw-text-black/40  tw-uppercase tw-font-black tw-tracking-widest">
            {t("studioOptions.mic.clickRecord")}
          </div>
        )}
      </div>{" "}
      {/* Timer */}
      <div className="tw-bg-gray-100 tw-p-5 tw-text-purple-700 tw-font-bold tw-rounded-xl tw-py-2 tw-flex tw-items-center tw-gap-2">
        {recordingBlobForUpload
          ? formatSeconds(recordedDuration)
          : formatSeconds(micRecordingTime)}
        {recordingBlobForUpload && !isMicRecording && (
          <FaCheck className="tw-text-green-500" />
        )}
      </div>
      {/* No separate preview player, integrated into buttons below */}
      {/* Loading Indicator */}
      {micLoading &&
        !audioPreviewUrl && ( // Show general loading only before preview is ready
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-mt-4">
            <Spinner className="tw-border-purple-700" size={6} />
            <span className="tw-text-sm tw-text-gray-500">
              {t("studioOptions.mic.processing")}
            </span>
          </div>
        )}
      {/* Action Button Area */}
      <div className="tw-flex tw-gap-2 tw-items-center tw-justify-center tw-w-full">
        {audioPreviewUrl && (
          <>
            {/* Play/Pause Button */}

            {/* Continue/Upload Button */}
            <Button
              onClick={handleMicUpload}
              className="tw-rounded-2xl tw-w-full tw-font-black tw-uppercase tw-text-base tw-h-12 tw-border tw-border-solid tw-border-purple-700 tw-border-b-4 tw-border-b-purple-900 active:tw-translate-y-0.5 active:tw-border-b-2 active:tw-scale-[0.98] tw-transition tw-duration-100 tw-ease-in-out disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              // Disable if no blob, loading, or actively recording (shouldn't happen here but safe)
              disabled={!recordingBlobForUpload || micLoading || isMicRecording}
            >
              {micLoading ? (
                <Spinner className="tw-border-white tw-border-3" size={4} />
              ) : (
                t("studioOptions.mic.continue")
              )}
            </Button>
            <SecondaryButton
              variant="secondary"
              onClick={togglePreviewPlayback}
              className="tw-rounded-2xl tw-flex-shrink-0 tw-p-3" // Adjust padding maybe
              disabled={micLoading} // Disable while uploading
              aria-label={
                isPreviewPlaying
                  ? t("studioOptions.mic.pausePreview")
                  : t("studioOptions.mic.playPreview")
              }
            >
              {isPreviewPlaying ? (
                <FaPause className="tw-w-5 tw-h-5" />
              ) : (
                <FaPlay className="tw-w-5 tw-h-5" />
              )}
            </SecondaryButton>
          </>
        )}
      </div>
    </div>
    // --- End Microphone Recording UI ---
  );
};

export default MicrophoneRecordView;
